"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUnsavedChanges } from "@/app/contexts/UnsavedChangesContext";
import { useRouter } from "next/navigation";

export function UnsavedChangesDialog() {
  const {
    showUnsavedDialog,
    setShowUnsavedDialog,
    pendingNavigation,
    setPendingNavigation,
    pendingNavigationData,
    setPendingNavigationData,
    resetAllForms,
  } = useUnsavedChanges();
  const router = useRouter();

  const handleConfirmNavigation = () => {
    setShowUnsavedDialog(false);
    resetAllForms();

    // Handle navigation if it's a navigation type
    if (pendingNavigation === "navigation" && pendingNavigationData?.href) {
      if (pendingNavigationData.method === "push") {
        router.push(pendingNavigationData.href);
      } else if (pendingNavigationData.method === "replace") {
        router.replace(pendingNavigationData.href);
      }
    }

    setPendingNavigation(null);
    setPendingNavigationData(null);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
    setPendingNavigationData(null);
  };

  const getActionText = () => {
    if (pendingNavigation === "tab") {
      return "Exit & Switch tabs";
    } else if (pendingNavigation === "section") {
      return "Exit & Navigate away";
    } else if (pendingNavigation === "back") {
      return "Exit & Go back";
    } else if (pendingNavigation === "cancel") {
      return "Exit & Cancel editing";
    } else if (pendingNavigation === "navigation") {
      return "Exit & Continue";
    }
    return "Exit & Leave";
  };

  return (
    <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-6 w-6 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <AlertDialogTitle className="text-xl font-semibold text-gray-900">
            Unsaved Changes Detected
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600">
            You have unsaved changes that will be lost if you continue.
            <br />
            <br />
            <span className="font-medium text-gray-900">
              Choose your action:
            </span>
            <br />
            <span className="text-sm">
              • <span className="font-medium">Stay</span> - Keep editing and
              save your changes
              <br />• <span className="font-medium">Exit</span> - Discard
              changes and continue with navigation
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-3 sm:flex-row sm:justify-center">
          <AlertDialogCancel
            onClick={handleCancelNavigation}
            className="w-full sm:w-auto bg-white border-2 border-[#2C5282] text-[#2C5282] hover:bg-[#2C5282] hover:text-white transition-colors duration-200 font-medium"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
            Stay & Keep Changes
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmNavigation}
            className="w-full sm:w-auto bg-[#2C5282] hover:bg-[#1A365D] text-white border-0 font-medium transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
              />
            </svg>
            {getActionText()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
